import { useState, useEffect } from "react";
import { Menu, RefreshCw, ChevronDown, Search, Plus, Phone, MessageSquare, Voicemail, Clock, AlertCircle, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import LogoutConfirmDialog from "@/components/common/LogoutConfirmDialog";
import NotificationBell from "@/components/common/NotificationBell";
import { GlobalTimelog } from "@/components/common/GlobalTimelog";
import { useSMSCount } from "@/hooks/sms/useSMSCount";
import { useVoicemailCount } from "@/hooks/voicemail/useVoicemailCount";
import { useHBoxCall } from "@/components/CallHandler/hooks/useHBoxCall";
import { clinicsGraphqlService } from "@/services/api/clinics/clinicsGraphqlService";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { APP_VERSION } from "@/config/envConfig";

interface AdminHeaderProps {
  onToggleSidebar: () => void;
}
export const AdminHeader = ({
  onToggleSidebar,
}: AdminHeaderProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasuraToken, token } = useAuth();
  const { toast } = useToast();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const { smsCount } = useSMSCount();
  const { voicemailCount } = useVoicemailCount();
  const { hboxCall } = useHBoxCall();
  const [engagementNumbers, setEngagementNumbers] = useState<any[]>([]);
  const [numberSources, setNumberSources] = useState<any[]>([]);
  const [allowedRoles, setAllowedRoles] = useState<string[]>([]);
  const [showVersionDialog, setShowVersionDialog] = useState(false);

  const userType = user?.type || 'ED';

  // Fetch enrollment numbers, number sources, and allowed roles on component mount
  useEffect(() => {
    const fetchDialerData = async () => {
      if (!hasuraToken) return;

      try {
        // Fetch allowed roles for outbound calling
        const roles = await clinicsGraphqlService.getOutboundCallRoles(hasuraToken);
        setAllowedRoles(roles);

        // Only fetch numbers if user has permission
        if (userType && roles.includes(userType)) {
          const numbers = await clinicsGraphqlService.getAllEnrollmentNumbers(token);
          setEngagementNumbers(numbers.outboundNumber);

          const sources = await clinicsGraphqlService.getClinicNumberSourceTypes(hasuraToken);
          setNumberSources(sources);
        }
      } catch (error) {
        console.error('Error fetching dialer data:', error);
      }
    };

    fetchDialerData();
  }, [hasuraToken, userType, token]);

  // Open outbound call dialer
  const openOutboundCall = () => {
    if (!hboxCall) {
      toast({
        title: 'Call System Not Ready',
        description: 'Please wait for the call system to initialize.',
        variant: 'destructive'
      });
      return;
    }

    if (engagementNumbers.length === 0) {
      toast({
        title: 'No Numbers Available',
        description: 'No enrollment numbers available for outbound calling.',
        variant: 'destructive'
      });
      return;
    }

    const modalOptions = {
      numbers: engagementNumbers?.length ? engagementNumbers?.map((number) => {return {phone_number: number.originNumber, number_source_id: number.numberSourceId}}) : [],
      numberSources: numberSources,
      callerId: user?.id,
      key: 'PEP'
    };

    hboxCall.showDialerModal(modalOptions);
  };

  const handleRefresh = () => {
    // Force reload of current page without navigating
    window.location.reload();
  };

  const handleLogoutClick = () => {
    setShowLogoutDialog(true);
  };

  const handleLogoutConfirm = async () => {
    await logout();
    setShowLogoutDialog(false);
    navigate("/");
  };

  const handleNotificationClick = () => {
    navigate('/transfer-patients-list');
  };

  const displayName = user?.fullName || '';
  return <header id="admin_header_container" className="sticky top-0 z-50 h-16 bg-[#f5f0fa] border-b border-purple-200/50 flex items-center px-4 w-full">
      <div className="flex items-center">
        <Button id="admin_header_toggle_sidebar_btn" variant="ghost" size="icon" onClick={onToggleSidebar} className="text-gray-500 hover:text-gray-700 hover:bg-purple-100/50 mr-4">
          <Menu className="h-5 w-5" strokeWidth={1.5} />
        </Button>
        
        <div className="flex items-center gap-2">
          <img id="admin_header_logo" src="/lovable-uploads/6823d161-b5be-421b-863a-12eaf2ea796e.png" alt="HBox Logo" className="h-9 w-auto" />
          <span className="text-2xl font-bold text-purple-900">HBox</span>
        </div>
      </div>
      
      <div className="flex-1" />

      <div className="flex items-center gap-3">

        {/* Global Timelog - Show for ES, CS, PE users */}
        {['ES', 'CS', 'PE'].includes(userType) && <GlobalTimelog />}

        <Button id="admin_header_refresh_btn" variant="ghost" onClick={handleRefresh} className="font-medium text-purple-600 hover:text-purple-700 hover:bg-purple-100/50 border border-purple-300 rounded-full px-4 h-9">
          <RefreshCw className="h-4 w-4 mr-2" strokeWidth={1.5} />
          Page Refresh
        </Button>

        {/* Outbound Call Button - Show for users with permission */}
        {allowedRoles.includes(userType) && (
          <Button
            id="admin_header_outbound_call_btn"
            onClick={openOutboundCall}
            variant="ghost"
            className="relative h-9 w-9 rounded-full font-medium text-purple-600 hover:text-purple-700 hover:bg-purple-100/50 border border-purple-300"
            title="Make Outbound Call"
          >
            <Phone className="h-4 w-4" strokeWidth={1.5} />
          </Button>
        )}
        
        {/* Transfer Patient Notifications - Show for ED users */}
        {/* {['PE', 'PA'].includes(userType) && (
          <NotificationBell onNotificationClick={handleNotificationClick} />
        )} */}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button id="admin_header_user_menu_btn" variant="ghost" className="flex items-center gap-2 hover:bg-purple-100/50 text-purple-700">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-purple-600 text-white text-sm">
                  {displayName.split(' ').map(n => n[0]).join('')}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col items-start">
                <span className="text-sm font-medium text-gray-700">{displayName}</span>
                <span className="text-xs text-gray-500">{user?.type || ''}</span>
              </div>
              <ChevronDown className="h-4 w-4 text-gray-400" strokeWidth={1.5} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent id="admin_header_user_menu_content" align="end" className="w-48 bg-white border border-gray-200 shadow-lg rounded-lg">
            <DropdownMenuItem id="admin_header_version_info_item" onClick={() => setShowVersionDialog(true)} className="hover:bg-purple-50 cursor-pointer">
              Version Info
            </DropdownMenuItem>
            <DropdownMenuItem id="admin_header_logout_item" onClick={handleLogoutClick} className="hover:bg-purple-50 cursor-pointer">
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <LogoutConfirmDialog 
        open={showLogoutDialog}
        onOpenChange={setShowLogoutDialog}
        onConfirm={handleLogoutConfirm}
      />

      {/* Version Info Dialog */}
      <Dialog open={showVersionDialog} onOpenChange={setShowVersionDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-purple-600" />
              Application Version
            </DialogTitle>
            <DialogDescription>
              Current application version information
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Current Version */}
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-sm font-medium text-gray-700">Version:</span>
              <span className="text-sm font-bold text-gray-900">
                {APP_VERSION}
              </span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </header>;
};